import type { Damage, Element, Poison, Slow, Stats } from '../data/schema';
import type { Rng } from './rng';
import { STAT_FOR_ELEMENT, type DamageSource, type HealSource, type Monster, type Pickup, type Player, type Summon, type Vec2 } from './types';
import { spawn, type World } from './world';

/** `mult` : multiplicateur final (bonus de méta du joueur) ; 1 pour les dégâts des monstres. */
export function rollDamage(rng: Rng, dmg: Damage, stats: Stats, resistances: Partial<Record<Element, number>>, mult = 1): number {
  const base = rng.int(dmg.min, dmg.max);
  const statKey = STAT_FOR_ELEMENT[dmg.element];
  const stat = statKey ? stats[statKey] : 0;
  const res = resistances[dmg.element] ?? 0;
  return Math.max(0, Math.floor(base * (100 + stat) / 100 * (100 - res) / 100 * mult));
}

/** Multiplicateur des dégâts infligés par le joueur à `target` : dégâts de méta, et dégâts sur les boss. */
export function playerDamageMult(world: World, target: Monster | Player): number {
  const m = world.meta;
  return (1 + m.damagePct / 100) * (target.kind === 'monster' && target.boss ? 1 + m.bossDamagePct / 100 : 1);
}

/** Ralentissement posé par le joueur sur un monstre : le bonus de méta renforce le facteur (borné à [0,1 ; 1]). */
export function applySlow(world: World, m: Monster, slow: Slow): void {
  const until = world.time + slow.duration;
  const bonus = world.meta.slowBonusPct;
  const base = bonus === 0 ? slow.factor : Math.min(1, Math.max(0.1, slow.factor * (1 - bonus / 100)));
  const factor = m.slow && world.time < m.slow.until ? Math.min(m.slow.factor, base) : base;
  m.slow = { factor, until: Math.max(until, m.slow?.until ?? 0) };
  world.events.push({ type: 'status', targetId: m.id, status: 'slow', duration: slow.duration });
}

/** Dégâts d'un sort multipliés de `pct` pour cent (arrondis). */
export const scaleDamage = (d: Damage, pct: number): Damage => ({ element: d.element, min: Math.round(d.min * (1 + pct / 100)), max: Math.round(d.max * (1 + pct / 100)) });

/** Bonus de dégâts du joueur en pourcentage : buffs actifs et trait Souffrance (croît quand les PV baissent). */
export function playerDamagePct(world: World, p: Player): number {
  let pct = 0;
  for (const b of p.buffs) if (b.until > world.time) pct += b.damagePct;
  const trait = world.content.classes[p.defId]?.trait;
  if (trait?.type === 'souffrance') pct += trait.maxDamagePct * (1 - p.hp / p.hpMax);
  return pct;
}

export function healPlayer(world: World, p: Player, amount: number, source: HealSource): number {
  if (!p.alive) return 0;
  const before = p.hp;
  p.hp = Math.min(p.hpMax, p.hp + amount);
  const healed = p.hp - before;
  if (healed > 0) world.events.push({ type: 'heal', amount: healed, source });
  return healed;
}

const poisonPerSecond = (po: { damage: Damage; interval: number }): number => (po.damage.min + po.damage.max) / 2 / po.interval;

/**
 * Pose un poison sur un monstre : le plus fort en dégâts par seconde gagne. À dégâts égaux (même poison réappliqué),
 * la durée est prolongée sans recaler le tic ; un poison plus faible est ignoré (l'actuel est gardé tel quel).
 */
export function applyPoison(world: World, m: Monster, poison: Poison): void {
  const until = world.time + poison.duration;
  const current = m.poison;
  if (current) {
    const dps = poisonPerSecond(poison);
    const currentDps = poisonPerSecond(current);
    if (dps < currentDps) return;
    if (dps === currentDps) {
      current.until = Math.max(current.until, until);
      world.events.push({ type: 'status', targetId: m.id, status: 'poison', duration: poison.duration });
      return;
    }
  }
  m.poison = { damage: { ...poison.damage }, interval: poison.interval, nextTick: world.time + poison.interval, until };
  world.events.push({ type: 'status', targetId: m.id, status: 'poison', duration: poison.duration });
}

/** Renvoie les dégâts réellement infligés (après bouclier, sans dépasser les PV restants). */
/** `source` : auteur du coup (joueur et tout ce qu'il pose ou invoque, familier, ou monstre), repris par l'événement `death`. */
export function applyDamage(world: World, target: Monster | Player | Summon, amount: number, element: Element, pos: Vec2, source: DamageSource = 'player'): number {
  if (!target.alive) return 0;
  if (target.kind === 'player' && target.shield) {
    if (world.time < target.shield.until) {
      const absorbed = Math.min(amount, target.shield.amount);
      target.shield.amount -= absorbed;
      amount -= absorbed;
    }
    if (target.shield.amount <= 0 || world.time >= target.shield.until) target.shield = null;
  }
  const inflicted = Math.min(amount, target.hp);
  target.hp = Math.max(0, target.hp - amount);
  world.events.push({ type: 'damage', targetId: target.id, amount, element, pos: { ...pos } });
  if (target.anim !== 'die' && target.anim !== 'attack') { target.anim = 'hit'; target.animTime = 0; }
  if (target.hp > 0) return inflicted;
  target.alive = false;
  target.anim = 'die';
  target.animTime = 0;
  world.events.push({ type: 'death', entityId: target.id, defId: target.defId, pos: { ...target.pos }, by: source });
  if (target.kind === 'monster') {
    const kamas = world.rng.int(target.kamas[0], target.kamas[1]);
    const mk = (pickupType: 'kamas' | 'xp', amount: number, dx: number): void => {
      if (amount <= 0) return;
      spawn<Pickup>(world, { kind: 'pickup', team: 'player', defId: pickupType, sprite: pickupType, pos: { x: target.pos.x + dx, y: target.pos.y }, vel: { x: 0, y: 0 }, radius: 8, facing: { x: 0, y: 0 }, hp: 1, hpMax: 1, alive: true, anim: 'idle', animTime: 0, pickupType, amount });
    };
    mk('xp', target.xp, -10);
    mk('kamas', kamas, 10);
  } else if (target.kind === 'player') {
    world.events.push({ type: 'playerDied' });
  }
  return inflicted;
}

/** Coup du joueur sur un monstre : jet de dégâts, ralentissement et poison si la cible survit, puis vol de vie sur les dégâts infligés. */
export function hitTarget(world: World, p: Player, target: Monster, dmg: Damage, opts: { lifestealPct: number; slow: Slow | null; poison: Poison | null }, pos: Vec2): number {
  const inflicted = applyDamage(world, target, rollDamage(world.rng, dmg, p.stats, target.resistances, playerDamageMult(world, target)), dmg.element, pos);
  if (target.alive) {
    if (opts.slow) applySlow(world, target, opts.slow);
    if (opts.poison) applyPoison(world, target, opts.poison);
  }
  const heal = Math.floor(inflicted * (opts.lifestealPct + p.lifestealPct) / 100);
  if (heal > 0) healPlayer(world, p, heal, 'lifesteal');
  return inflicted;
}
